import { Volume2, ArrowLeft, ChevronRight } from "lucide-react";
import PracticeRecording from "./PracticeRecording";
import { useProfileContext } from "../../../context/ProfileContext";
import WordImage from "./WordImage";
import type { TeacherPracticeStudent } from "../../../services/teacherPracticeSession";
import TaskProgress, { type TaskStatus } from "./TaskProgress";

type Props = {
  recordingRef: React.RefObject<any>;
  onSkip: () => void;
  onSubmitAudio: () => void;
  isUploading: boolean;
  uploadStatus: string;
  isTeacherSession?: boolean;
  teacherStudents?: TeacherPracticeStudent[];
  activeStudentId?: number | null;
  isSwitchingStudent?: boolean;
  onTeacherStudentChange?: (studentId: number) => void;
  onHome: () => void;
  taskStatus: TaskStatus;
};

const AudioPracticePage = ({
  recordingRef,
  onSkip,
  onSubmitAudio,
  isUploading,
  uploadStatus,
  isTeacherSession = false,
  teacherStudents = [],
  activeStudentId = null,
  isSwitchingStudent = false,
  onTeacherStudentChange,
  onHome,
  taskStatus,
}: Props) => {
  const { currentWord } = useProfileContext();

  const speakAudioBlob = (file: Blob) => {
    const audioUrl = URL.createObjectURL(file);
    const audio = new Audio(audioUrl);
    audio.play();
    audio.onended = () => {
      URL.revokeObjectURL(audioUrl);
    };
  };

  if (!currentWord) {
    return <p>No word selected for practice.</p>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-200 via-pink-100 to-yellow-100 p-4 flex items-start justify-center overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-xl p-4 max-w-xl w-full my-auto">
        {isTeacherSession && teacherStudents.length > 0 && (
          <div className="mb-2">
            <label
              htmlFor="teacher-student-switch"
              className="mb-1 block text-xs font-bold text-[#7a58a5]"
            >
              Student
            </label>
            <select
              id="teacher-student-switch"
              value={activeStudentId ?? ""}
              onChange={(event) =>
                onTeacherStudentChange?.(Number(event.target.value))
              }
              disabled={isSwitchingStudent}
              className="w-full rounded-xl border border-[#d6b8f0] bg-white px-3 py-2 text-sm font-medium text-[#5f4786] outline-none focus:border-[#8650bd] disabled:cursor-not-allowed disabled:bg-purple-50"
            >
              {teacherStudents.map((student) => (
                <option key={student.studentId} value={student.studentId}>
                  {student.studentName}, {student.classId}-{student.section}
                </option>
              ))}
            </select>
            {isSwitchingStudent && (
              <p className="mt-1 text-xs font-medium text-[#7a58a5]">
                Switching student...
              </p>
            )}
          </div>
        )}

        <TaskProgress taskStatus={taskStatus} />

        {/* Title */}
        <h1 className="text-2xl font-bold text-purple-700 text-center mb-2">
          Say the Word
        </h1>

        {/* Word Card - Image and Pronunciation */}
        <div className="bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-50 rounded-2xl p-3 mb-3 border-2 border-yellow-300 shadow-sm">
          {/* Word Image */}
          <div className="flex justify-center mb-2">
            <div className="bg-white rounded-2xl p-2 shadow-md">
              <WordImage />
            </div>
          </div>

          {/* Pronunciation Buttons */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              onClick={() => speakAudioBlob(currentWord.audio_english!)}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-3 px-4 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <span className="text-base">{currentWord.english}</span>
              <Volume2 className="w-5 h-5" />
            </button>

            <button
              onClick={() => speakAudioBlob(currentWord.audio_telugu!)}
              className="bg-gradient-to-r from-blue-500 to-green-500 text-white font-bold py-3 px-4 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <span className="text-base">{currentWord.telugu}</span>
              <Volume2 className="w-5 h-5" />
            </button>
          </div>

          {/* Instruction Text */}
          <p className="text-center text-purple-600 font-semibold text-sm">
            Tap to hear the pronunciation
          </p>
        </div>

        {/* Recording Card */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-3 mb-3 border-2 border-purple-200 shadow-sm">
          <h2 className="text-base font-bold text-purple-700 text-center mb-2">
            Record yourself
          </h2>

          {/* Recording Component */}
          <PracticeRecording
            ref={recordingRef}
            key={currentWord.english + "recording"}
            englishWord={currentWord.english}
            teluguWord={currentWord.telugu}
            onSubmit={onSubmitAudio}
            isSubmitting={isUploading}
          />
        </div>

        {/* Upload Status */}
        {uploadStatus && (
          <div
            className={`mb-3 p-2 rounded-xl font-semibold text-center text-sm ${
              uploadStatus.includes("✅")
                ? "bg-green-100 text-green-700 border-2 border-green-300"
                : uploadStatus.includes("❌")
                ? "bg-red-100 text-red-700 border-2 border-red-300"
                : "bg-blue-100 text-blue-700 border-2 border-blue-300"
            }`}
          >
            {uploadStatus}
          </div>
        )}

        {/* Bottom Navigation Buttons */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onHome}
            className="bg-white border-2 border-purple-300 text-purple-700 font-bold py-2.5 px-3 rounded-2xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </button>

          <button
            onClick={onSkip}
            className="bg-white border-2 border-purple-300 text-purple-700 font-bold py-2.5 px-3 rounded-2xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
          >
            Skip Word
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioPracticePage;
